// src/hooks/useResourceLimits.js
import { useQuery, useQueryClient } from 'react-query';
import { useDataProvider } from 'react-admin';
import { createTenantQueryKey } from '../utils/tenant';

// Hook to check if a resource can be created based on max-settings
export const useResourceLimits = (resource) => {
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  
  const resourceToSettingKey = {
    'contacts': 'max_contacts',
    'calendars': 'max_calendars'
  };
  
  const settingKey = resourceToSettingKey[resource];
  
  const query = useQuery(
    createTenantQueryKey(['resourceLimits', resource]),
    async () => {
      if (!settingKey) {
        // If no max setting exists for this resource, allow creation
        return { canCreate: true, current: 0, max: null };
      }
      
      // Fetch current count of resources and limits data in parallel
      const [resourceList, settingsData] = await Promise.all([
        dataProvider.getList(resource, {
          pagination: { page: 1, perPage: 1000 }, // Get all records to count them
          sort: { field: 'id', order: 'ASC' },
          filter: {}
        }),
        dataProvider.getList('settings', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'key', order: 'ASC' },
          filter: {}
        })
      ]);

      const currentCount = resourceList.total || resourceList.data.length;

      // Get max from settings data
      const maxSetting = settingsData.data.find(setting => setting.key === settingKey);
      const maxCount = maxSetting ? parseInt(maxSetting.value, 10) : null;
      
      const canCreate = maxCount === null || currentCount < maxCount;
      
      return {
        canCreate,
        current: currentCount,
        max: maxCount,
        maxSetting: maxSetting
      };
    },
    {
      enabled: !!resource,
      staleTime: 5 * 1000, // 5 seconds - more frequent updates
      cacheTime: 10 * 1000, // Keep in cache for 10 seconds
      refetchOnWindowFocus: true, // Refetch when window gains focus
      refetchInterval: 10 * 1000, // Auto-refetch every 10 seconds
    }
  );

  // Set up a method to manually invalidate this query
  const invalidate = () => {
    queryClient.invalidateQueries(createTenantQueryKey(['resourceLimits', resource]));
  };

  return {
    ...query,
    invalidate
  };
};

// Hook to get the limit status message for display
export const useResourceLimitMessage = (resource) => {
  const { data, isLoading, error } = useResourceLimits(resource);
  
  if (isLoading) return 'Checking limits...';
  if (error) return 'Error checking limits';
  if (!data) return '';
  
  const { canCreate, current, max } = data;
  
  if (max === null) return ''; // No limit set
  
  if (!canCreate) {
    return `Maximum limit reached (${current}/${max})`;
  }
  
  return `${current}/${max} used`;
};